import { useFormContext, Controller } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { I18nInput } from "@/components/articles/i18n-input";
import { ImageUploadField } from "@/components/articles/image-upload-field";
import { SocialAccountsField } from "@/components/articles/social-accounts-field";
import { PaywallDivider } from "@/components/articles/paywall-divider";
import type { PremiumArticleEditFormValues } from "@/lib/validation/article-schema";

interface SyncInfo {
  title: string;
  hasUpdate: boolean;
  lastSyncedAt?: string;
  onSync: () => void;
  isSyncing: boolean;
}

interface EditorPanelProps {
  contentHtml: string;
  onFreeContentChange: (html: string) => void;
  onTogglePreviewWidth?: (enabled: boolean) => void;
  initialFreeHtml?: string;
  syncInfo?: SyncInfo;
}

function formatSyncTime(dateStr?: string): string {
  if (!dateStr) return "정보 없음";
  return new Date(dateStr).toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function EditorPanel({ contentHtml, onFreeContentChange, onTogglePreviewWidth, initialFreeHtml, syncInfo }: EditorPanelProps) {
  // In edit mode the form uses PremiumArticleEditFormValues (no title field).
  // In create mode it uses PremiumArticleMetaFormValues (includes title).
  // We use PremiumArticleEditFormValues as the base since it's the common subset.
  const {
    control,
    formState: { errors },
  } = useFormContext<PremiumArticleEditFormValues & Record<string, unknown>>();

  const isEditMode = !!syncInfo;

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 items-center border-b px-3">
        <span className="text-sm font-medium">콘텐츠 & 설정</span>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {/* Source File Info (edit mode only) */}
        {syncInfo && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">원본 파일 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <span className="text-muted-foreground text-xs">제목</span>
                <p className="text-sm font-medium">{syncInfo.title}</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">마지막 동기화</span>
                <p className="text-sm">{formatSyncTime(syncInfo.lastSyncedAt)}</p>
              </div>
              {syncInfo.hasUpdate ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    원본 파일이 업데이트되었습니다
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    동기화 버튼을 눌러 제목과 콘텐츠를 최신 상태로 업데이트하세요.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={syncInfo.onSync}
                    disabled={syncInfo.isSyncing}
                  >
                    {syncInfo.isSyncing ? "동기화 중..." : "동기화"}
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground text-xs">최신 상태입니다</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Paywall Divider */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">페이월 설정</CardTitle>
          </CardHeader>
          <CardContent>
            <PaywallDivider contentHtml={contentHtml} onFreeContentChange={onFreeContentChange} onTogglePreviewWidth={onTogglePreviewWidth} initialFreeHtml={initialFreeHtml} />
            {errors.freeContentHtml && (
              <p className="text-destructive mt-2 text-sm">{errors.freeContentHtml.message}</p>
            )}
          </CardContent>
        </Card>

        {/* Basic Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">기본 정보</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Title field: only in create mode (edit mode syncs title from source file) */}
            {!isEditMode && (
              <Controller
                name={"title" as string}
                control={control}
                render={({ field }) => (
                  <I18nInput
                    label="제목"
                    value={field.value}
                    onChange={field.onChange}
                    error={
                      (errors as Record<string, { ko?: { message?: string } }>)
                        .title?.ko?.message
                    }
                    required
                  />
                )}
              />
            )}
            <Controller
              name="excerpt"
              control={control}
              render={({ field }) => (
                <I18nInput
                  label="요약"
                  value={field.value}
                  onChange={field.onChange}
                  multiline
                  rows={3}
                  error={errors.excerpt?.ko?.message}
                  required
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Thumbnail */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">썸네일</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Controller
              name="thumbnail.url"
              control={control}
              render={({ field }) => (
                <ImageUploadField
                  label="썸네일 이미지"
                  value={field.value || { ko: "", en: null }}
                  onChange={field.onChange}
                  category="post-thumbnail"
                  error={errors.thumbnail?.url?.ko?.message}
                  required
                />
              )}
            />
            <Controller
              name="thumbnail.alt"
              control={control}
              render={({ field }) => (
                <I18nInput
                  label="대체 텍스트"
                  value={field.value || { ko: "", en: null }}
                  onChange={field.onChange}
                  error={errors.thumbnail?.alt?.ko?.message}
                  required
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Author Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">작성자 정보</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Controller
              name="authorInfo.name"
              control={control}
              render={({ field }) => (
                <I18nInput
                  label="작성자 이름"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.authorInfo?.name?.ko?.message}
                  required
                />
              )}
            />
            <Controller
              name="authorInfo.profileImageURL"
              control={control}
              render={({ field }) => (
                <ImageUploadField
                  label="프로필 이미지 (선택)"
                  value={field.value || { ko: "", en: null }}
                  onChange={field.onChange}
                  category="post-thumbnail"
                />
              )}
            />
            <Controller
              name="authorInfo.biography"
              control={control}
              render={({ field }) => (
                <I18nInput
                  label="작성자 소개 (선택)"
                  value={field.value || { ko: "", en: null }}
                  onChange={field.onChange}
                  multiline
                  rows={3}
                />
              )}
            />
            <Separator />
            <SocialAccountsField control={control} name="authorInfo.socialAccounts" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
